/**
 * Provider resubmittals and the lead-row disposition (Isaiah Cotto,
 * 2026-10-01). Every type mirrors what `routes/manifestResubmittals.js` and
 * `services/advocate/resubmittals.js` return.
 *
 * Its own file on purpose: manifestApi.ts is another builder's surface.
 */
import { apiRequest } from '../../services/api';

const BASE = '/manifest';

export type ResubmittalReason = 'REEVALUATE_CURRENT_YEAR' | 'REEVALUATE_12_PLUS_MONTHS' | 'RESUBMIT_TO_PROVIDER';

/** How each hand-back reads on the Resubmittals list. */
export const RESUBMITTAL_REASON_LABEL: Record<ResubmittalReason, string> = {
  REEVALUATE_CURRENT_YEAR: 'Re-evaluate this year',
  REEVALUATE_12_PLUS_MONTHS: 'Re-evaluate after 12+ months',
  RESUBMIT_TO_PROVIDER: 'Resubmit to the provider',
};

export type Resubmittal = {
  id: string;
  patient_id: string;
  originating_case_id: string | null;
  reason_code: ResubmittalReason;
  reason_note: string;
  requested_at: string;
  facility_id: string | null;
  facility_name: string | null;
  /** CounselEAR's Preferred Clinic, sent when eMerge has no building (2026-10-14). Shown as "CounselEAR clinic: X (building not confirmed)". */
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
  requested_facility_id: string | null;
  requested_facility_name: string | null;
  first_name: string | null;
  last_name: string | null;
  counselear_id: string | null;
  requested_by_name: string | null;
  originating_advocate_name: string | null;
  originating_word: string | null;
  originating_closed_on: string | null;
  /** False until CounselEAR has sent a name for the resident. */
  name_received: boolean;
  /** The resident is now at a different building from the one it was asked for at. */
  moved_since: boolean;
};

/** Where the provider will see them next. `null` when nothing is recorded for today or later. */
export type NextVisit = { on: string; source: 'VISIT_PLAN' | 'SCHEDULING'; state: 'PLANNED' | 'CONFIRMED' | null };

export type ResubmittalGroup = {
  facility_id: string | null;
  facility_name: string | null;
  next_visit: NextVisit | null;
  provider: string | null;
  items: Resubmittal[];
};

export type ResubmittalList = {
  groups: ResubmittalGroup[];
  total: number;
  /** May this caller mark rows added, cancel them, and close leads from the Manifest? The server decides again on every call. */
  can_act: boolean;
  /** May this caller send a decided review line back to its provider, signed (docs/record-flow/send-back-design.md §3.4)? Held AND the manifest gate open AND the key configured. */
  can_send_back?: boolean;
  disposition_words: DispositionWord[];
};

export type PlaceResult = { placed: string[]; already: string[]; refused: { id: string; code: string }[]; notFound: string[] };

/**
 * R5: what "Disposition…" offers on a lead row. The PA's own words; the
 * server holds the same list and refuses anything else (400 WORD_NOT_OFFERED).
 */
export type DispositionWord =
  | 'DNQ' | 'NITWL' | 'REEVALUATE - CURRENT YEAR' | 'REEVALUATE - 12+ MONTHS' | 'RESUBMIT TO PROVIDER'
  | 'NEEDS VERIFICATION' | 'DECEASED' | 'DISCHARGED' | 'HOSPICE';

/**
 * `toVerify` (D-29, Dominique Genua 2026-10-02): the resident goes on Needs
 * verification for a VA to check benefits; the reason is what to check.
 */
export const DISPOSITION_WORDS: ReadonlyArray<{ word: DispositionWord; label: string; reasonRequired: boolean; toProvider: boolean; toVerify?: boolean }> = [
  { word: 'DNQ', label: 'Does not qualify (DNQ)', reasonRequired: true, toProvider: false },
  { word: 'NITWL', label: 'Not interested (NITWL)', reasonRequired: true, toProvider: false },
  { word: 'REEVALUATE - CURRENT YEAR', label: 'Re-evaluate this year (back to the provider)', reasonRequired: true, toProvider: true },
  { word: 'REEVALUATE - 12+ MONTHS', label: 'Re-evaluate after 12+ months (back to the provider)', reasonRequired: true, toProvider: true },
  { word: 'RESUBMIT TO PROVIDER', label: 'Resubmit to the provider', reasonRequired: true, toProvider: true },
  { word: 'NEEDS VERIFICATION', label: 'Needs verification (a VA checks benefits)', reasonRequired: true, toProvider: false, toVerify: true },
  { word: 'DECEASED', label: 'Deceased', reasonRequired: false, toProvider: false },
  { word: 'DISCHARGED', label: 'Discharged', reasonRequired: false, toProvider: false },
  { word: 'HOSPICE', label: 'Hospice', reasonRequired: false, toProvider: false },
];

export type DispositionResult = {
  word: string; stage: string; closed: boolean; replayed: boolean;
  resubmittal?: { id: string; state: 'PENDING' | 'PLACED' | 'CANCELLED'; created: boolean };
  /** D-29: the resident's one open verification request. `created: false` when a VA already had them. */
  verification?: { id: string; state: 'OPEN' | 'QUALIFIED' | 'NOT_QUALIFIED'; created: boolean };
  siblingsCarried?: Array<{ caseId: string }>;
  siblingsNotCarried?: Array<{ caseId: string; stage: string }>;
};

export const resubmittalsApi = {
  /** PENDING resubmittals in the caller's buildings, grouped by building, with the next visit and the provider. */
  list: () => apiRequest<ResubmittalList>('GET', `${BASE}/resubmittals`),

  /** "Mark added to the manifest": the VA put these residents on the workbook. Idempotent. */
  place: (ids: string[], note?: string | null) =>
    apiRequest<PlaceResult>('POST', `${BASE}/resubmittals/place`, { ids, note: note || null }),

  /** Cancel one, with a reason. */
  cancel: (id: string, reason: string) =>
    apiRequest<{ id: string; state: 'CANCELLED'; replayed: boolean }>('POST', `${BASE}/resubmittals/${id}/cancel`, { reason }),

  /** R5: close a lead with a disposition. The same act as the PA's status change, carried to the resident's other open records. */
  disposition: (caseId: string, body: { status: DispositionWord; reason?: string | null; idempotency_key: string }) =>
    apiRequest<DispositionResult>('POST', `${BASE}/cases/${caseId}/disposition`, body),
};

/** A refusal in words a reviewer can act on. A missing status means the server was never reached. */
export function refusalWords(r: { code?: string; message?: string; error?: string; status?: number }, fallback: string): string {
  if (r.status === undefined) return 'Could not reach eMerge. Nothing was changed.';
  if (r.code === 'REASON_REQUIRED') return 'Say why. The reason stays on the record.';
  if (r.code === 'NOT_FOUND') return 'Not found, or outside your buildings. Refresh the list.';
  if (r.code === 'ALREADY_PLACED') return 'This resident was already added to the manifest; there is nothing to cancel.';
  if (r.code === 'TRANSITION_INVALID') return 'This record cannot take that status from where it is. Open the case to see why.';
  if (r.code === 'NO_RESIDENT') return 'This record is not linked to a resident, so it cannot go back to the provider.';
  if (r.code === 'DENY_NO_CAPABILITY') return 'You can see this list; acting on it needs the lead-placement permission.';
  if (r.status >= 500) return 'eMerge failed while recording this. Nothing was changed, and it is not a permissions problem.';
  return r.message && r.message !== 'Request failed' ? r.message : fallback;
}

/** An idempotency key per act, so a double click is one act. */
export const actKey = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
